import { useState } from 'react';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);

  const navLinks = [
    { label: 'Home', href: '/' },
    { label: 'Spreadsheet', href: '#what-is' },
    { label: 'Reviews', href: '#featured-guides' },
    { label: 'Shipping', href: '#comparison' },
    { label: 'Guides', href: '#seo-articles' },
    { label: 'Categories', href: '#categories' },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/60">
      <nav className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16 md:h-20">
        {/* Logo */}
        <a href="/" className="flex items-center gap-2 group cursor-pointer">
          <span className="font-heading text-xl md:text-2xl font-bold tracking-tight text-foreground-900">
            Basetao <span className="italic font-normal text-foreground-600">Sheet</span>
          </span>
        </a>

        {/* Desktop Nav Links */}
        <div className="hidden md:flex items-center gap-6">
          {navLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              className={`text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                link.label === 'Home'
                  ? 'text-foreground-900 hover:text-primary-600'
                  : 'text-foreground-700 hover:text-primary-600'
              }`}
            >
              {link.label}
            </a>
          ))}
        </div>

        {/* Desktop CTA Buttons */}
        <div className="hidden md:flex items-center gap-3">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer transition-all duration-200 bg-primary-500 text-background-50 hover:bg-primary-600"
          >
            <i className="ri-shopping-bag-3-line"></i>
            Shopping Guide
          </a>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer transition-all duration-200 bg-foreground-900 text-background-50 hover:bg-foreground-800"
          >
            <i className="ri-external-link-line"></i>
            Visit Main Site
          </a>
        </div>

        {/* Mobile Menu Toggle */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
          aria-label="Toggle menu"
        >
          <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-xl text-foreground-900`}></i>
        </button>
      </nav>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/60 max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-4 flex flex-col gap-1">
            {/* Main Navigation */}
            <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider px-1 mb-2">Navigation</p>
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-2.5 text-foreground-700 text-sm font-medium py-2.5 px-3 rounded-lg hover:bg-background-100 hover:text-primary-600 transition-all cursor-pointer"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className={`text-xs ${
                    link.label === 'Home' ? 'ri-home-4-line' :
                    link.label === 'Spreadsheet' ? 'ri-database-2-line' :
                    link.label === 'Reviews' ? 'ri-star-line' :
                    link.label === 'Shipping' ? 'ri-ship-line' :
                    link.label === 'Guides' ? 'ri-book-open-line' :
                    'ri-grid-line'
                  }`}></i>
                </span>
                {link.label}
              </a>
            ))}

            {/* Divider */}
            <div className="border-t border-background-200/60 my-3"></div>

            {/* Category Navigation */}
            <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider px-1 mb-2">Product Categories</p>
            <div className="grid grid-cols-2 gap-1 mb-3">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileOpen(false)}
                  className="text-foreground-600 text-xs py-2 px-3 rounded-lg hover:bg-accent-50 hover:text-accent-700 transition-all cursor-pointer flex items-center gap-1.5"
                >
                  <span className="w-1 h-1 rounded-full bg-accent-400 flex-shrink-0"></span>
                  <span className="truncate">{cat.name}</span>
                </a>
              ))}
            </div>

            {/* Divider */}
            <div className="border-t border-background-200/60 my-3"></div>

            {/* CTA Buttons */}
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center justify-center gap-2 px-4 py-3 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer bg-primary-500 text-background-50 hover:bg-primary-600"
            >
              <i className="ri-shopping-bag-3-line"></i>
              Shopping Guide
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center justify-center gap-2 px-4 py-3 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer bg-foreground-900 text-background-50 hover:bg-foreground-800 mt-2"
            >
              <i className="ri-external-link-line"></i>
              Visit Main Site
            </a>
          </div>
        </div>
      )}
    </header>
  );
}